import type { Board } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { createAngle } from '../angle/angle.create';
import type { AngleArgs, AngleResult } from '../angle/angle.model';
import { createCircle } from '../circle/circle.create';
import type { CircleArgs, CircleResult } from '../circle/circle.model';
import { createCurve } from '../curve/curve.create';
import type { CurveArgs, CurveResult } from '../curve/curve.model';
import { createGlider } from '../glider/glider.create';
import type { GliderArgs, GliderResult } from '../glider/glider.model';
import { createLine } from '../line/line.create';
import type { LineArgs, LineResult } from '../line/line.model';
import { createPoint } from '../point/point.create';
import type { PointArgs, PointResult } from '../point/point.model';
import { createPolygon } from '../polygon/polygon.create';
import type { PolygonArgs, PolygonResult } from '../polygon/polygon.model';
import { DEFAULT_ELEMENT_THEME } from '../theme/theme.defaults';
import type { ElementTheme } from '../theme/theme.model';
import type { ElementCreatorContext } from './board.types';
import { deepMerge } from './deepMerge';

/**
 * Options for creating an element factory.
 */
export interface ElementFactoryOptions {
  /** Custom theme overrides */
  theme?: Partial<ElementTheme>;
}

export interface ElementFactory {
  point(args: PointArgs): PointResult;
  line(args: LineArgs): LineResult;
  polygon(args: PolygonArgs): PolygonResult;
  curve(args: CurveArgs): CurveResult;
  angle(args: AngleArgs): AngleResult;
  circle(args: CircleArgs): CircleResult;
  glider(args: GliderArgs): GliderResult;
  getTheme(): ElementTheme;
  getContext(): ElementCreatorContext;
}

/**
 * Create an element factory for creating JSXGraph elements with consistent theming.
 *
 * The factory provides a convenient API for creating elements with:
 * - Automatic theme resolution
 * - Color name to CSS color conversion
 * - Style propagation for composite elements
 * - Label creation and binding
 *
 * @param board - The JSXGraph board
 * @param container - The container HTML element
 * @param themeService - Theme service for color resolution
 * @param options - Optional factory configuration
 * @returns Element factory instance
 *
 * @example
 * ```typescript
 * const factory = createElementFactory(board, container, themeService);
 *
 * // Create a triangle with labeled vertices
 * const triangle = factory.polygon({
 *   vertices: [[0, 0], [4, 0], [2, 3]],
 *   name: 'ABC',
 *   vertexLabels: true,
 *   style: {
 *     fillColor: 'sky',
 *     vertexPerIndex: {
 *       0: { color: 'red' },
 *       1: { color: 'green' },
 *       2: { color: 'blue' },
 *     },
 *   },
 * });
 *
 * // Create an angle at vertex A showing degrees
 * const angle = factory.angle({
 *   a: triangle.vertices[1], // B
 *   vertex: triangle.vertices[0], // A
 *   b: triangle.vertices[2], // C
 *   name: 'BAC',
 *   label: { contentVariant: 'name+deg' },
 * });
 * ```
 */
export function createElementFactory(
  board: Board,
  container: HTMLElement,
  themeService: JsxgraphThemeService,
  options?: ElementFactoryOptions,
): ElementFactory {
  const theme = deepMerge(DEFAULT_ELEMENT_THEME, options?.theme) as ElementTheme;
  const ctx: ElementCreatorContext = { board, container };

  return {
    point: (args) => createPoint(ctx, themeService, theme, args),
    line: (args) => createLine(ctx, themeService, theme, args),
    polygon: (args) => createPolygon(ctx, themeService, theme, args),
    curve: (args) => createCurve(ctx, themeService, theme, args),
    angle: (args) => createAngle(ctx, themeService, theme, args),
    circle: (args) => createCircle(ctx, themeService, theme, args),
    glider: (args) => createGlider(ctx, themeService, theme, args),
    getTheme: () => theme,
    getContext: () => ctx,
  };
}
